import type { ComponentPropsWithoutRef } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { HelpLogo } from "@/components/brand/help-logo";
import { LEGAL_DRAFT_NOTICE } from "@/lib/constants";

interface LegalDocumentPageProps {
  /** The document's name, used for the tab title. The Markdown carries its own `# H1`. */
  title: string;
  /** The document text, imported with Vite's `?raw` suffix. */
  markdown: string;
}

/**
 * Typography for a legal document, in the arbitrary-descendant-class idiom of `@trillion/ui`'s
 * `MARKDOWN_PROSE` (`message-parts.tsx`): the repo has no `@tailwindcss/typography`, so `.prose`
 * would be inert. Colours come from the theme tokens only, so light and dark both hold.
 */
const LEGAL_PROSE =
  "text-base leading-7 " +
  "[&>*:first-child]:mt-0 " +
  "[&_h1]:font-display [&_h1]:text-3xl [&_h1]:font-semibold [&_h1]:leading-tight [&_h1]:mt-8 [&_h1]:mb-4 " +
  "[&_h2]:font-display [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-10 [&_h2]:mb-3 " +
  "[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-2 " +
  "[&_p]:my-3 " +
  "[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-6 " +
  "[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-6 " +
  "[&_li]:mt-1 " +
  "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 " +
  "[&_strong]:font-semibold " +
  "[&_table]:w-full [&_table]:border-collapse [&_table]:text-sm " +
  "[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:align-top [&_th]:font-semibold " +
  "[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top " +
  "[&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:text-muted-foreground";

/**
 * `/`-relative hrefs (the two documents cross-link) stay in the SPA as router links; anything else
 * opens in a new tab without handing the destination a window reference.
 */
function DocumentLink({ href = "", children, node: _node, ...rest }: ComponentPropsWithoutRef<"a"> & { node?: unknown }) {
  if (href.startsWith("/") && !href.startsWith("//")) {
    return (
      <Link to={href} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
}

/** A wide table scrolls inside its own container, so the page never scrolls sideways on a phone. */
function DocumentTable({ node: _node, ...rest }: ComponentPropsWithoutRef<"table"> & { node?: unknown }) {
  return (
    <div className="my-4 overflow-x-auto">
      <table {...rest} />
    </div>
  );
}

const MARKDOWN_COMPONENTS: Components = { a: DocumentLink, table: DocumentTable };

/**
 * One legal document as a standalone page. It sits outside `ChatLayout`, so it never creates a
 * session, resolves intent or touches conversation storage. Until legal sign-off it shows the draft
 * notice and asks search engines not to index it; React 19 hoists the `<title>` and `<meta>` into
 * `document.head`.
 */
export function LegalDocumentPage({ title, markdown }: LegalDocumentPageProps) {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <title>{`${title} — help.com`}</title>
      <meta name="robots" content="noindex, nofollow" />
      <div className="mx-auto max-w-3xl px-4 py-6 sm:py-10">
        <header className="mb-6">
          <Link to="/" aria-label="help.com home" className="inline-block">
            <HelpLogo />
          </Link>
        </header>
        <p
          role="note"
          className="mb-8 rounded-lg border border-border bg-muted px-4 py-3 text-sm font-medium"
        >
          {LEGAL_DRAFT_NOTICE}
        </p>
        <article className={LEGAL_PROSE}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
            {markdown}
          </ReactMarkdown>
        </article>
      </div>
    </div>
  );
}
